import React from 'react';
import { Box, AppBar, Toolbar, Typography, Container, IconButton, Link, Divider, Grid, GlobalStyles } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';

interface LayoutProps {
  children: React.ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const handleXClick = () => {
    window.open('https://x.com', '_blank');
  };

  return (
    <>
      <GlobalStyles
        styles={{
          '*::-webkit-scrollbar': {
            width: '8px',
            height: '8px',
          },
          '*::-webkit-scrollbar-track': {
            background: '#16181C',
          },
          '*::-webkit-scrollbar-thumb': {
            background: '#2F3336',
            borderRadius: '4px',
          },
          '*::-webkit-scrollbar-thumb:hover': {
            background: '#3E4144',
          },
          '*': {
            scrollbarWidth: 'thin',
            scrollbarColor: '#2F3336 #16181C',
          },
        }}
      />
      <Box className="min-h-screen flex flex-col" sx={{ backgroundColor: '#000000' }}>
        <AppBar 
          position="fixed" 
          sx={{ 
            backgroundColor: 'rgba(0, 0, 0, 0.85)', 
            backdropFilter: 'blur(12px)',
            borderBottom: '1px solid rgba(47, 51, 54, 0.5)'
          }}
        >
          <Toolbar>
            <IconButton 
              edge="start" 
              aria-label="X logo" 
              onClick={handleXClick}
              sx={{ 
                mr: 2,
                width: 34,
                height: 34,
                padding: 0
              }}
            >
              <img 
                src="/xicon.webp" 
                alt="X Logo" 
                style={{ 
                  width: '100%', 
                  height: '100%',
                  objectFit: 'contain'
                }} 
              />
            </IconButton>
            <Link
              component={RouterLink}
              to="/"
              sx={{
                color: '#E7E9EA',
                textDecoration: 'none',
                '&:hover': {
                  textDecoration: 'none',
                  opacity: 0.8
                },
                flexGrow: 1
              }}
            >
              <Typography variant="h6" component="div">
                X Downloader Pro
              </Typography>
            </Link>
          </Toolbar>
        </AppBar>
        <Toolbar /> {/* This empty Toolbar acts as a spacer */}
        
        <Container
          maxWidth="lg"
          className="flex-grow"
          sx={{ py: { xs: 2, sm: 3, md: 6 }, px: { xs: 2, sm: 3 } }}
        >
          {children}
        </Container>
        
        <Box 
          component="footer" 
          sx={{ 
            backgroundColor: '#16181C',
            borderTop: '1px solid #2F3336',
            py: 4
          }}
        >
          <Container maxWidth="lg">
            <Grid container spacing={4}>
              {/* Navigation Column */}
              <Grid item xs={12} md={4}>
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                  <Typography variant="subtitle1" sx={{ color: '#E7E9EA', fontWeight: 'bold', mb: 1 }}>
                    Navigation
                  </Typography>
                  <Link
                    component={RouterLink}
                    to="/"
                    sx={{
                      color: '#1D9BF0',
                      textDecoration: 'none',
                      '&:hover': { textDecoration: 'underline' }
                    }}
                  >
                    Home
                  </Link>
                  <Link
                    component={RouterLink}
                    to="/downloader"
                    sx={{
                      color: '#1D9BF0',
                      textDecoration: 'none',
                      '&:hover': { textDecoration: 'underline' }
                    }}
                  >
                    Downloader
                  </Link>
                  <Link
                    component={RouterLink}
                    to="/gallery"
                    sx={{
                      color: '#1D9BF0',
                      textDecoration: 'none',
                      '&:hover': { textDecoration: 'underline' }
                    }}
                  >
                    Account media
                  </Link>
                  <Link
                    component={RouterLink}
                    to="/legal"
                    sx={{
                      color: '#1D9BF0',
                      textDecoration: 'none',
                      '&:hover': { textDecoration: 'underline' }
                    }}
                  >
                    Terms & Privacy
                  </Link>
                </Box>
              </Grid>

              {/* Branding Column */}
              <Grid item xs={12} md={4}>
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                    <Link
                      href="https://x.com"
                      target="_blank"
                      sx={{
                        display: 'flex',
                        '&:hover': { opacity: 0.8 }
                      }}
                    >
                      <img 
                        src="/xicon.webp" 
                        alt="X Logo" 
                        style={{ 
                          width: '32px',
                          height: '32px',
                          objectFit: 'contain'
                        }} 
                      />
                    </Link>
                    <Link
                      component={RouterLink}
                      to="/"
                      sx={{
                        color: '#E7E9EA',
                        textDecoration: 'none',
                        '&:hover': { opacity: 0.8 }
                      }}
                    >
                      <Typography variant="h6">
                        X Downloader Pro
                      </Typography>
                    </Link>
                  </Box>
                  <Typography variant="body2" sx={{ color: '#71767B', textAlign: 'center' }}>
                    Download photos and videos from X posts instantly.
                  </Typography>
                </Box>
              </Grid>

              {/* Legal Column */}
              <Grid item xs={12} md={4}>
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                  <Typography variant="subtitle1" sx={{ color: '#E7E9EA', fontWeight: 'bold', mb: 1 }}>
                    Legal
                  </Typography>
                  <Link
                    href="https://twitter.com/tos"
                    target="_blank"
                    sx={{
                      color: '#1D9BF0',
                      textDecoration: 'none',
                      '&:hover': { textDecoration: 'underline' }
                    }}
                  >
                    X Terms of Service
                  </Link>
                  <Link
                    href="https://twitter.com/privacy"
                    target="_blank"
                    sx={{
                      color: '#1D9BF0',
                      textDecoration: 'none',
                      '&:hover': { textDecoration: 'underline' }
                    }}
                  >
                    X Privacy Policy
                  </Link>
                </Box>
              </Grid>
            </Grid>

            <Divider sx={{ borderColor: '#2F3336', my: 4 }} />

            {/* Bottom Disclaimer */}
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="body2" sx={{ color: '#71767B', mb: 2 }}>
                This app is for educational purposes only. Users are solely responsible for compliance with X's terms of service.
              </Typography>
              <Typography variant="body2" sx={{ color: '#F4212E', fontWeight: 'bold' }}>
                BY USING THIS SERVICE, YOU ACCEPT ALL RESPONSIBILITY FOR COMPLIANCE WITH APPLICABLE LAWS AND TERMS.
              </Typography>
            </Box>
          </Container>
        </Box>
      </Box>
    </>
  );
};

export default Layout; 